import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import BackButton from "../../common/back-button";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_DAILY_HOLDINGS_ENDPOINT } from "../../config/urls";
import { ColorsConstants } from "../../cssUtils/constants";

const fmt = (n) =>
  n == null
    ? null
    : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const pct = (n) => (n == null ? null : `${Number(n).toFixed(2)}%`);

const units = (n) =>
  n == null
    ? null
    : Number(n).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const normalizeHolding = (h) => ({
  ...h,
  dividend: h.divident ?? h.dividend,
  dividend_reinvested: h.divident_reinvested ?? h.dividend_reinvested,
});

const Field = ({ label, value, color }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{ mt: 0.5, color: color || "text.primary" }}
    >
      {value ?? "—"}
    </Typography>
  </Box>
);

const Section = ({ title, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        {children}
      </Grid>
    </Paper>
  </Box>
);

const DailyHoldingDetailView = ({ holding: initialHolding, onBack }) => {
  const { getValidToken } = useAuth();
  const [holding, setHolding] = useState(initialHolding);

  const getHeaders = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(
          `${MFD_DAILY_HOLDINGS_ENDPOINT}${initialHolding.id}/`,
          { headers },
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        setHolding(normalizeHolding(data));
      } catch {
        // keep initial data on error
      }
    };
    fetchDetail();
  }, [initialHolding.id, getHeaders]);

  const gainColor =
    holding.gain_loss == null
      ? undefined
      : holding.gain_loss >= 0
        ? ColorsConstants.successText
        : ColorsConstants.error;
  const returnColor =
    holding.absolute_return == null
      ? undefined
      : holding.absolute_return >= 0
        ? ColorsConstants.successText
        : ColorsConstants.error;
  const xirrColor =
    holding.xirr == null
      ? undefined
      : holding.xirr >= 0
        ? ColorsConstants.successText
        : ColorsConstants.error;

  return (
    <Box sx={{ p: 3 }}>
      <BackButton onClick={onBack} />

      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Holding Details
        </Typography>
        <Typography
          sx={{ ...typography.bodyLarge, color: "text.secondary", mt: 0.5 }}
        >
          {holding.client_code} &nbsp;·&nbsp; {holding.pan} &nbsp;·&nbsp;{" "}
          {holding.date}
        </Typography>
      </Box>

      <Section title="Identity">
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Date" value={holding.date} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Source" value={holding.source} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field label="PAN" value={holding.pan} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Client Code" value={holding.client_code} />
        </Grid>
      </Section>

      <Section title="Branch &amp; Advisor">
        <Grid item xs={12} sm={6} md={4}>
          <Field label="Branch Name" value={holding.branch_name} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field label="Advisor Name" value={holding.advisor_name} />
        </Grid>
      </Section>

      <Section title="Portfolio">
        <Grid item xs={12} sm={6} md={3}>
          <Field label="First Investment Date" value={holding.first_investment_date} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Investment" value={fmt(holding.investment)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Portfolio Value" value={fmt(holding.portfolio_value)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Balance Units" value={units(holding.balance_units)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field
            label="Avg. Holding Days"
            value={
              holding.avg_holding_days == null
                ? null
                : `${holding.avg_holding_days} days`
            }
          />
        </Grid>
      </Section>

      <Section title="Returns">
        <Grid item xs={12} sm={6} md={3}>
          <Field label="Dividend" value={fmt(holding.dividend)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Field
            label="Dividend Reinvested"
            value={fmt(holding.dividend_reinvested)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Field
            label="Gain / Loss"
            value={fmt(holding.gain_loss)}
            color={gainColor}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Field
            label="Absolute Return"
            value={pct(holding.absolute_return)}
            color={returnColor}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Field label="XIRR" value={pct(holding.xirr)} color={xirrColor} />
        </Grid>
      </Section>
    </Box>
  );
};

export default DailyHoldingDetailView;
